import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import {
    EMPTY,
    Observable,
    Subject,
    Subscription,
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    map,
    merge,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs';
import { TimerService } from 'src/app/shared/services/timerService';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AppState } from 'src/app/viewbid/state/app-state';
import { Store } from '@ngrx/store';
import { SelectedLotState } from '../../state/selected-lot-state/selected-lot.reducer';
import { selectSelectedLotState } from '../../state/selected-lot-state/selected-lot.selectors';
import {
    SelectLotAction,
    LotFetchedSuccessfulAction,
} from '../../state/selected-lot-state/selected-lot.actions';
import { TranslateService } from '@ngx-translate/core';
import {
    AssetInner,
    AuctionCatalogsService,
    AuctionDetail,
    GetAuctionById200Response,
    LotItemUserNote,
    ListResponse,
    LotEditHistory,
    LotAttributes,
    AuctionLotDetail,
    LotDetail,
    Reminder,
    NotificationsService,
    BiddingService,
    GetMyBidHistory200Response,
    UserBidHistoryResponse,
    PostBidLotState,
    GetWatcherByIdResponse,
    AdjacentAuctionLot,
    AdjacentAuctionLotsResponse,
    GetAdjacentAuctionLots200Response,
} from '@viewbid/ts-angular-client';
import { VbSnackbarService } from '@viewbid/ui';
import { BiddingUtils, reconcileHighBidder } from '@/vb-utils/bidding.util';
import { WatchlistService } from 'src/app/viewbid/shared/services/watchlist.service';
import {
    addToWatchlist,
    removeFromWatchlist,
} from '../../../profiles/state/watchlist-page-state/watchlist-page-state.actions';
import { TokenStorageService, User } from 'viewbid-shared-lib';
import { selectIsAdmin, selectUser } from 'src/app/viewbid/state/app-selectors';
import { SocketService } from 'src/app/viewbid/shared/services/socket.service';
import { ChatBoxService } from 'src/app/viewbid/shared/components/chatbox/service/chatbox.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Auction, fmtDateTime, Lot, VbDocumentItem } from '@viewbid/ui';
import { FileDownloadService } from '../../services/file-download.service';
import {
    LotResponseWithProxy,
    auctionResponseToAuction,
    lotResponseToLot,
    normalizeLotRow,
} from 'src/app/viewbid/shared/utils/lot-card-mapper.util';
import {
    LOT_GRACE_MS,
    LotCountdownTracker,
    hasTimePassed,
    isTimeAhead,
} from 'src/app/viewbid/shared/utils/lot-countdown.util';
import { ServerClockService } from 'src/app/viewbid/shared/services/server-clock.service';
import { canViewClosedLot } from 'src/app/viewbid/shared/utils/closed-lot-access.util';
import {
    LotCardBidService,
    nextBidAmountFor,
} from 'src/app/viewbid/shared/services/lot-card-bid.service';

type ProductDialog =
    | 'confirm'
    | 'notes'
    | 'alerts'
    | 'history'
    | 'bidHistory'
    | 'offer';
type ChipTone = 'neutral' | 'positive' | 'warn' | 'ink';

// The lot-by-id endpoint returns the canonical AuctionLotDetail; the page
// still reads the richer legacy LotDetail fields (editHistory, attributes,
// isHighestBidAProxy, …) and the mapper consumes the list-shaped
// LotResponseWithProxy. The intersection satisfies all three.
type LotItemModel = LotResponseWithProxy & LotDetail & AuctionLotDetail;

// AssetInner.type is either a bare extension ('pdf') or a MIME string —
// used to restore a save-as extension when the document name lacks one.
const MIME_EXTENSIONS: Record<string, string> = {
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/zip': 'zip',
    'text/plain': 'txt',
    'text/csv': 'csv',
    'image/jpeg': 'jpg',
    'image/png': 'png',
};

@Component({
    selector: 'vb-product-details',
    templateUrl: './vb-product-details.component.html',
    styleUrls: ['./vb-product-details.component.scss'],
})
export class VBProductDetailsComponent implements OnDestroy, OnInit {
    public isLoading: boolean = true;
    public unableToLoad: boolean = false;
    public lotItem: LotItemModel = {} as LotItemModel;
    public auctionId: string = '';
    public lotId: string = '';
    public auction!: AuctionDetail;
    public currentUserId: string = '';
    /** The signed-in user's REAL username. Socket broadcasts mask the
     *  high-bidder name for everyone, but the server's own visibility rule
     *  (resolveVisibleUsername) shows the high bidder their unmasked name —
     *  this lets socket updates apply the same rule client-side. */
    private currentUsername: string = '';
    public isLoggedIn: boolean = false;
    public isClosed: boolean = false;
    /** True the instant the clock hits zero — disables bidding immediately,
     *  while the lot stays visible (showing 0) for the grace beat before the
     *  closed swap. Mirrors vb-lot-card's grace behaviour. */
    public biddingEnded: boolean = false;
    private closeGraceTimer: ReturnType<typeof setTimeout> | null = null;

    // Library view-models derived from lotItem / auction.
    public mappedLot: Lot = { title: '' };
    public mappedAuction: Auction = {};
    public userHasBid: boolean = false;
    /** True while a bid placed from the panel is in flight, so the panel can
     *  disable its CTAs and avoid duplicate/too-low bids from a double tap. */
    public bidLoading: boolean = false;
    public closesAtLabel: string = '';
    public opensAtLabel: string = '';

    // Lot documents (from lotItem.documents — the ui Lot type doesn't carry
    // them) and the row currently downloading, mirrored into the list's busy
    // state.
    public documentItems: VbDocumentItem[] = [];
    public downloadingDocId: string | null = null;

    // Dialog orchestration (the app owns which modal is open).
    public activeDialog: ProductDialog | null = null;
    public pendingBidAmount: number = 0;
    /** Socket updates reassign mappedLot while the confirm dialog is open, so
     *  recomputing at post time would label the bid against an unseen minimum. */
    private pendingBidMinimum: number = 0;
    public offerId?: string;

    // Sub-feature data (consumed by the dialog bodies).
    public lotItemUserNotes: LotItemUserNote[] = [];
    public lotEditHistory: LotEditHistory[] = [];
    public savedReminders: Reminder[] = [];

    /** Prev/next lots within the auction (catalog order) for the breadcrumb
     *  navigation buttons; null until fetched (buttons hidden). */
    public adjacentLots: AdjacentAuctionLotsResponse | null = null;
    /** Lot the adjacent fetch has run for (fetch once per lot). */
    private adjacentFetchedForId?: string;

    public readonly countdownTracker: LotCountdownTracker;

    @ViewChild('bidPanel', { read: ElementRef })
    private bidPanelRef?: ElementRef<HTMLElement>;

    private watchlistId?: string;
    /** Lot whose socket resource we've already subscribed to (subscribe once). */
    private socketSubscribedLotId?: string;
    private lotTimerSubscription?: Subscription;
    /** True while the getLotById fetch is in flight. Socket updates that land in
     *  this window can't match the not-yet-loaded lot, so they're buffered below
     *  and overlaid once the lot commits — otherwise a stale snapshot can show
     *  "winning" after a socket already said "outbid" (refresh required). */
    private lotLoadInFlight: boolean = false;
    /** Socket-pushed field patches per lot collected during an in-flight load,
     *  re-applied over the fetched lot when it commits (keyed by auctionLotsId). */
    private readonly socketPatchesDuringLoad: Map<
        string,
        Partial<LotItemModel>
    > = new Map();
    /** ?makeAnOffer=true arrives before the lot/auction load; held here until
     *  both are in so the offer gate can be checked instead of opening blind. */
    private pendingOfferOpen: boolean = false;
    private closedAccessAwaitsClock: boolean = false;
    private isAdmin: boolean = false;
    private readonly subscription: Subscription = new Subscription();
    private readonly destroy$: Subject<void> = new Subject<void>();

    constructor(
        private readonly store: Store<AppState>,
        private readonly route: ActivatedRoute,
        public readonly translate: TranslateService,
        private readonly auctionService: AuctionCatalogsService,
        private readonly biddingService: BiddingService,
        private readonly snackbarService: VbSnackbarService,
        private readonly notificationsService: NotificationsService,
        private readonly utils: BiddingUtils,
        private readonly watchlistService: WatchlistService,
        private readonly lotCardBidService: LotCardBidService,
        private readonly timerService: TimerService,
        private readonly socketService: SocketService,
        private readonly router: Router,
        private readonly chatBoxService: ChatBoxService,
        private readonly modalService: BsModalService,
        private readonly sanitizer: DomSanitizer,
        private readonly location: Location,
        private readonly tokenStorageService: TokenStorageService,
        private readonly fileDownloadService: FileDownloadService,
        private readonly serverClock: ServerClockService,
    ) {
        this.countdownTracker = new LotCountdownTracker(this.timerService, () =>
            this.serverClock.now(),
        );
    }

    ngOnInit(): void {
        this.countdownTracker.start();

        this.store
            .select(selectIsAdmin)
            .pipe(takeUntil(this.destroy$))
            .subscribe((isAdmin: boolean) => {
                this.isAdmin = isAdmin;
            });

        this.utils
            .getCurrentUserId()
            .pipe(takeUntil(this.destroy$))
            .subscribe((currentUserId: string) => {
                this.currentUserId = currentUserId;
                this.isLoggedIn = !!currentUserId;
                // The closed-lot access check may have deferred while the
                // user id was still loading — settle it now that it's known.
                this.enforceClosedLotAccess();
            });

        this.store
            .select<User | null>(selectUser)
            .pipe(takeUntil(this.destroy$))
            .subscribe((user: User | null) => {
                this.currentUsername =
                    (user?.['username'] as string | undefined) ?? '';
            });

        this.route.params
            .pipe(takeUntil(this.destroy$))
            .subscribe((params: Params) => {
                // Release the old lot on navigation, not on the next commit: its
                // timer callback would otherwise re-commit it over the new load.
                if (params['lotId'] !== this.lotId) {
                    this.releaseLotSubscription();
                }
                this.auctionId = params['auctionId'];
                this.lotId = params['lotId'];
                if (this.auctionId) {
                    this.fetchAuction();
                }
                if (this.lotId) {
                    // Open the load window: buffer socket updates until the
                    // fetched lot commits in onLotState.
                    this.lotLoadInFlight = true;
                    this.socketPatchesDuringLoad.clear();
                    // Clear stale prev/next while the new lot loads — they
                    // belong to the lot being navigated away from.
                    this.adjacentLots = null;
                    this.adjacentFetchedForId = undefined;
                    this.store.dispatch(
                        new SelectLotAction({ lotId: this.lotId }),
                    );
                }
            });

        this.subscription.add(
            this.store
                .select(selectSelectedLotState)
                .subscribe((state: SelectedLotState) => this.onLotState(state)),
        );

        this.route.params
            .pipe(
                map((params: Params) => params['lotId'] as string),
                distinctUntilChanged(),
                switchMap((lotId: string) => {
                    this.lotItemUserNotes = [];
                    this.savedReminders = [];
                    this.userHasBid = false;
                    return combineLatest([
                        this.utils
                            .getCurrentUserId()
                            .pipe(distinctUntilChanged()),
                        this.store.select(selectSelectedLotState).pipe(
                            map((state: SelectedLotState) => state.result),
                            filter(
                                (
                                    lot: SelectedLotState['result'],
                                ): lot is AuctionLotDetail =>
                                    !!lot &&
                                    !(lot instanceof Error) &&
                                    (lot.lotId ?? (lot as LotItemModel).id) ===
                                        lotId,
                            ),
                            take(1),
                        ),
                    ]).pipe(
                        switchMap(
                            ([currentUserId, lot]: [
                                string,
                                AuctionLotDetail,
                            ]) =>
                                currentUserId
                                    ? merge(
                                          this.getUserNotes(lotId),
                                          this.getReminders(lot.auctionLotsId),
                                          this.enrichUserBid(lot.auctionLotsId),
                                      )
                                    : EMPTY,
                        ),
                    );
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.subscription.add(
            this.timerService.timerComplete$.subscribe(
                (auctionLotId: string) => {
                    if (this.lotItem?.auctionLotsId !== auctionLotId) {
                        return;
                    }
                    // Disable bidding immediately (mirrors vb-lot-card), then
                    // flip to closed after the shared grace beat.
                    this.biddingEnded = true;
                    // Dismiss an in-flight bid/offer dialog so a confirmation
                    // can't land a bid after the clock has hit zero.
                    if (
                        this.activeDialog === 'offer' ||
                        this.activeDialog === 'confirm'
                    ) {
                        this.closeDialog();
                    }
                    // A lot already ended on load (end well in the past) skips
                    // the cosmetic beat and closes at once; a live clock that
                    // just hit zero gets the grace window.
                    const endMs: number = this.lotItem.toDatetime
                        ? new Date(this.lotItem.toDatetime).getTime()
                        : 0;
                    const alreadyEnded: boolean =
                        endMs > 0 &&
                        this.serverClock.now() - endMs > LOT_GRACE_MS;
                    if (alreadyEnded) {
                        this.clearCloseGrace();
                        this.isClosed = true;
                    } else {
                        this.startCloseGrace();
                    }
                },
            ),
        );

        // Anti-snipe: if an extension pushes the end time back into the future
        // during the grace beat, cancel the pending close and resume bidding.
        this.subscription.add(
            this.timerService.timerReset$.subscribe(
                ({ id, newEndTime }: { id: string; newEndTime: string }) => {
                    if (
                        this.lotItem?.auctionLotsId !== id ||
                        hasTimePassed(newEndTime, this.serverClock.now())
                    ) {
                        return;
                    }
                    this.resumeBidding();
                },
            ),
        );

        this.serverClock.corrected$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.followClockCorrection());

        this.socketService.resource$
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: string) => this.handleSocketEvent(data));

        // Reconnect REST backstop: refetch the visible auction and lot.
        this.socketService.reconnected$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.refetchOnReconnect());

        this.route.queryParams
            .pipe(takeUntil(this.destroy$))
            .subscribe((params: Params) => {
                this.offerId = params['offerId'];
                if (params['showNotes']) {
                    this.open('notes');
                }
                if (params['makeAnOffer']) {
                    this.pendingOfferOpen = true;
                    this.tryOpenPendingOffer();
                }
            });
    }

    ngOnDestroy(): void {
        this.snackbarService.dismissAll();
        this.countdownTracker.stop();
        this.clearCloseGrace();
        this.releaseLotSubscription();
        this.subscription.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private releaseLotSubscription(): void {
        this.lotTimerSubscription?.unsubscribe();
        this.lotTimerSubscription = undefined;
        this.socketSubscribedLotId = undefined;
    }

    /** Starts the grace beat: keeps the lot visible (showing 0, bidding off)
     *  for LOT_GRACE_MS, then flips to closed. */
    private startCloseGrace(): void {
        this.clearCloseGrace();
        this.closeGraceTimer = setTimeout(() => {
            this.closeGraceTimer = null;
            this.isClosed = true;
        }, LOT_GRACE_MS);
    }

    private resumeBidding(): void {
        this.clearCloseGrace();
        this.biddingEnded = false;
        this.isClosed = false;
    }

    private followClockCorrection(): void {
        const lotId: string | undefined = this.lotItem?.auctionLotsId;
        const endsAt: string | undefined = this.lotItem?.toDatetime;
        if (!lotId || !endsAt) return;
        if (hasTimePassed(endsAt, this.serverClock.now())) {
            this.syncClosedStateFromLot();
        } else if (this.biddingEnded || this.isClosed) {
            this.resumeBidding();
            this.timerService.resetTimer(lotId, endsAt);
        }
        if (this.closedAccessAwaitsClock) {
            this.enforceClosedLotAccess();
        }
    }

    private clearCloseGrace(): void {
        if (this.closeGraceTimer !== null) {
            clearTimeout(this.closeGraceTimer);
            this.closeGraceTimer = null;
        }
    }

    /**
     * Derives the closed / bidding-ended state from the loaded lot's end time,
     * so a lot that already ended when the page opens is shut out (bid controls
     * off, closed block shown) — matching the timer flow that runs when a lot
     * closes while the page is open. This only ever closes: a still-open lot is
     * left to the live timer flow, and an anti-snipe extension that reopens the
     * lot is handled by timerReset$. The lot ended before we arrived, so there
     * is no live grace beat to play — close at once.
     */
    private syncClosedStateFromLot(): void {
        if (!hasTimePassed(this.lotItem?.toDatetime, this.serverClock.now())) {
            return;
        }
        this.clearCloseGrace();
        this.biddingEnded = true;
        this.isClosed = true;
    }

    // --- Data loading -------------------------------------------------------

    // Reconnect resync: re-run the auction and lot REST loads.
    private refetchOnReconnect(): void {
        if (this.auctionId) {
            this.fetchAuction();
        }
        if (this.lotId) {
            this.lotLoadInFlight = true;
            this.socketPatchesDuringLoad.clear();
            this.store.dispatch(new SelectLotAction({ lotId: this.lotId }));
        }
    }

    private fetchAuction(): void {
        this.auctionService.getAuctionById(this.auctionId).subscribe({
            next: (res: GetAuctionById200Response) => {
                this.auction = res.data;
                this.mappedAuction = auctionResponseToAuction(this.auction);
                if (this.auction?.id) {
                    this.socketService.subscribeToResources([
                        { resourceId: this.auction.id, listen: true },
                    ]);
                }
                this.rebuildView();
                this.tryOpenPendingOffer();
            },
            error: () => {
                this.snackbarService.error(
                    this.translate.instant(
                        'AuctionsPage.getAuctionDetailsError',
                    ),
                );
            },
        });
    }

    /** Opens the offer dialog requested via query param once the lot and
     *  auction are loaded — and only if offers are actually available. */
    private tryOpenPendingOffer(): void {
        if (!this.pendingOfferOpen) return;
        if (!this.lotItem.auctionLotsId || !this.auction) return;
        this.pendingOfferOpen = false;
        if (this.showMakeAnOffer) {
            this.open('offer');
        }
    }

    private onLotState(state: SelectedLotState): void {
        // A late commit of the previous lot (live update after the new lot's
        // load failed) must not render it, or take bids, under this lot's URL.
        if (state.result && !(state.result instanceof Error)) {
            const resultLotId: string | undefined =
                state.result.lotId ?? (state.result as LotItemModel).id;
            if (resultLotId && resultLotId !== this.lotId) {
                return;
            }
        }
        this.isLoading = state.isLoading;

        if (state.hasError && state.errorCode === 1180) {
            this.lotLoadInFlight = false;
            this.socketPatchesDuringLoad.clear();
            this.snackbarService.system(
                this.translate.instant('LotDetails.lotClosed'),
            );
            // Landing redirect — replace the dead lot URL in history so the
            // browser Back button doesn't bounce straight back here.
            this.router.navigateByUrl('/home', { replaceUrl: true });
            return;
        }

        this.unableToLoad = state.hasError;
        if (!state.hasError && state.result === null) {
            this.isLoading = true;
            return;
        }
        if (!state.result || state.result instanceof Error) {
            this.lotLoadInFlight = false;
            this.socketPatchesDuringLoad.clear();
            return;
        }

        // Collapse the canonical wire aliases (currentBid, currentHighBidderId,
        // auctionLotStatus) onto the legacy names this page reads and mutates.
        // Committing the row raw leaves the canonical keys in place, and since
        // wireRead prefers them, they'd shadow every later legacy write — socket
        // PAGE_LOAD patches and optimistic bids would never change the shown
        // price or win/lose state.
        this.lotItem = normalizeLotRow(state.result) as LotItemModel;
        // Load completion: overlay any socket updates that arrived while the
        // fetch was in flight so a live bid mid-load isn't lost to the snapshot.
        if (this.lotLoadInFlight) {
            this.lotLoadInFlight = false;
            const patch: Partial<LotItemModel> | undefined = this.lotItem
                .auctionLotsId
                ? this.socketPatchesDuringLoad.get(this.lotItem.auctionLotsId)
                : undefined;
            if (patch) {
                this.lotItem = { ...this.lotItem, ...patch };
            }
            this.socketPatchesDuringLoad.clear();
        }
        this.lotEditHistory = this.lotItem.editHistory ?? [];

        this.fetchAdjacentLots();

        // Every PAGE_LOAD re-enters here through the store, so subscribe once per lot.
        if (
            this.lotItem.auctionLotsId &&
            this.socketSubscribedLotId !== this.lotItem.auctionLotsId
        ) {
            this.releaseLotSubscription();
            this.socketSubscribedLotId = this.lotItem.auctionLotsId;
            // Also joins the lot's room, which carries the lot-scoped events
            // (WATCHER_COUNT_UPDATE, NEW_WATCHER_FROM_BID) the auction room lacks.
            this.lotTimerSubscription =
                this.timerService.subscribeToTimerEvents(
                    [this.lotItem.auctionLotsId],
                    (newEndTime: string) => {
                        this.store.dispatch(
                            new LotFetchedSuccessfulAction({
                                result: {
                                    ...this.lotItem,
                                    toDatetime: newEndTime,
                                },
                            }),
                        );
                    },
                );
        }

        this.syncClosedStateFromLot();
        if (this.enforceClosedLotAccess()) {
            return;
        }
        this.rebuildView();
        this.tryOpenPendingOffer();
    }

    /**
     * Closed lots are only viewable by their winner — the high bidder with
     * the reserve satisfied (the same rule that gates lot-card and My-bidding
     * row clicks) — and by admins. Anyone else landing here (deep link,
     * stale card, back button) is bounced to the auction's lot list. Returns
     * true when it navigated away.
     *
     * Only load-time closes are policed (isClosed via syncClosedStateFromLot
     * or an already-ended clock) — a lot that closes live while the page is
     * open keeps its locked closed state so the viewer sees the outcome.
     */
    private enforceClosedLotAccess(): boolean {
        this.closedAccessAwaitsClock = false;
        if (!this.isClosed || !this.lotItem?.auctionLotsId) {
            return false;
        }
        if (!this.serverClock.canJudgePassed(this.lotItem.toDatetime)) {
            this.closedAccessAwaitsClock = true;
            return false;
        }
        // A signed-in session whose user id hasn't landed yet can't be judged
        // — hold off; the getCurrentUserId subscription re-runs this guard.
        if (!this.currentUserId && this.tokenStorageService.getToken()) {
            return false;
        }
        if (
            canViewClosedLot(this.lotItem, {
                id: this.currentUserId,
                isAdmin: this.isAdmin,
            })
        ) {
            return false;
        }
        this.snackbarService.system(
            this.translate.instant('LotDetails.closedLotUnavailable'),
        );
        // replaceUrl: this redirect fires on arrival, so a pushed entry would
        // leave the closed-lot URL in history and Back would just bounce
        // forward again — replace it so Back returns to the real previous page.
        if (this.auctionId) {
            this.router.navigate(['/auctions', this.auctionId], {
                replaceUrl: true,
            });
        } else {
            this.router.navigateByUrl('/home', { replaceUrl: true });
        }
        return true;
    }

    /** Recomputes the library view-models + countdown targets from app data. */
    private rebuildView(): void {
        this.mappedLot = lotResponseToLot(this.lotItem);
        if (this.auction) {
            this.mappedAuction = auctionResponseToAuction(this.auction);
        }
        this.documentItems = (this.lotItem.documents ?? [])
            .filter((doc: AssetInner): boolean => !!doc.url)
            .map(
                (doc: AssetInner, index: number): VbDocumentItem => ({
                    id: doc.id || doc.url || `doc-${index}`,
                    name: doc.name || 'Document',
                    type: doc.type,
                    url: doc.url,
                }),
            );
        this.closesAtLabel = fmtDateTime(this.lotItem.toDatetime);
        this.opensAtLabel = fmtDateTime(this.auction?.fromDatetime);

        if (this.mappedLot.auctionLotsId) {
            this.countdownTracker.setLots([
                {
                    auctionLotId: this.mappedLot.auctionLotsId,
                    fromDatetime: this.auction?.fromDatetime,
                    toDatetime: this.lotItem.toDatetime,
                },
            ]);
        }
    }

    /** Fetches the clicked document (public CloudFront URL) and hands it to
     *  the browser's save-file flow; the row shows busy until it settles. */
    onDownloadDocument(doc: VbDocumentItem): void {
        if (!doc.url || this.downloadingDocId) {
            return;
        }
        this.downloadingDocId = doc.id;
        this.fileDownloadService
            .downloadFile(doc.url, this.documentFilename(doc))
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (): void => {
                    this.downloadingDocId = null;
                },
                error: (): void => {
                    this.downloadingDocId = null;
                    this.snackbarService.error(
                        this.translate.instant('LotDetails.downloadError'),
                    );
                },
            });
    }

    /** Save-as filename: the document name, with an extension restored from
     *  [type] when the name doesn't already carry one. */
    private documentFilename(doc: VbDocumentItem): string {
        const name: string = doc.name.trim() || 'document';
        if (/\.[A-Za-z0-9]{1,5}$/.test(name) || !doc.type) {
            return name;
        }
        const type: string = doc.type.trim().toLowerCase();
        const extension: string = type.includes('/')
            ? (MIME_EXTENSIONS[type] ?? '')
            : type;
        return extension ? `${name}.${extension}` : name;
    }

    /** Loads the viewer's standing max bid so the panel can show winning/your-max. */
    private enrichUserBid(
        auctionLotsId: string,
    ): Observable<GetMyBidHistory200Response> {
        return this.biddingService.getMyBidHistory(1, 100, auctionLotsId).pipe(
            tap((response: GetMyBidHistory200Response) => {
                const mine: UserBidHistoryResponse | undefined =
                    response.data?.find(
                        (bid: UserBidHistoryResponse) =>
                            bid.auctionLotId === auctionLotsId,
                    );
                this.userHasBid = !!mine;
                this.lotItem = {
                    ...this.lotItem,
                    maximumProxyBid: mine?.userBid,
                };
                this.rebuildView();
            }),
            catchError(() => EMPTY),
        );
    }

    // --- Bid flow -----------------------------------------------------------

    onQuickBid(): void {
        // When the viewer is already the high bidder, the panel's mobile bar
        // labels this action "Raise max" — raises go through the proxy form
        // (desktop disables quick bid entirely in this state), so bring that
        // form into view instead of submitting a redundant quick bid.
        if (this.isWinning) {
            this.bidPanelRef?.nativeElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
            return;
        }
        this.startBid(nextBidAmountFor(this.mappedLot));
    }

    private get isWinning(): boolean {
        return (
            !!this.currentUserId &&
            this.mappedLot.highBidderId === this.currentUserId
        );
    }

    onProxyBid(amount: number): void {
        this.startBid(amount);
    }

    private startBid(amount: number): void {
        if (!this.currentUserId || !amount) {
            return;
        }
        this.pendingBidAmount = amount;
        this.pendingBidMinimum = nextBidAmountFor(this.mappedLot);
        if (this.utils.shouldShowBidConfirmation(this.auctionId)) {
            this.activeDialog = 'confirm';
        } else {
            this.placeBid(amount);
        }
    }

    onBidConfirmed(dontAsk: boolean): void {
        if (dontAsk) {
            this.utils.setSkipBidConfirmation(this.auctionId);
        }
        this.placeBid(this.pendingBidAmount);
        this.activeDialog = null;
    }

    onBidRounded(): void {
        this.snackbarService.system(
            this.translate.instant('Bidding.bidRoundedDown'),
        );
    }

    private placeBid(amount: number): void {
        if (
            this.biddingEnded ||
            !this.currentUserId ||
            !this.mappedLot.auctionLotsId ||
            !this.auctionId
        ) {
            return;
        }
        this.bidLoading = true;
        this.lotCardBidService.placeConfirmedBid(
            {
                auctionId: this.auctionId,
                auctionLotsId: this.mappedLot.auctionLotsId,
                lotId: this.mappedLot.lotId ?? this.mappedLot.id,
                lotTitle: this.mappedLot.title,
                lotNumber: this.mappedLot.number,
                buyerPremium: this.auction?.buyerPremium ?? 0,
                amount,
                minimumBidAmount: this.pendingBidMinimum,
                pageName: 'LOT_DETAILS',
                pageLocation: 'MAIN_INPUT_FIELD',
            },
            this.currentUserId,
            (placed: boolean, lotState?: PostBidLotState) => {
                this.bidLoading = false;
                if (placed) {
                    this.applyOptimisticBid(amount, lotState);
                }
            },
        );
    }

    private applyOptimisticBid(
        amount: number,
        lotState?: PostBidLotState,
    ): void {
        this.userHasBid = true;
        const storedLeaderIsCurrentUser: boolean =
            this.lotItem.highBidderId === this.currentUserId;
        // A winner raising their own proxy max is a top-up: the server records
        // no new bid row (current bid and bid count are unchanged), so bumping
        // the count here overstates it — and nothing authoritative ever
        // corrects a too-high count because socket updates only RAISE it (see
        // the monotonic guard in the socket handler). Genuine bids still get
        // the optimistic +1; the top-up keeps the count as-is.
        const isProxyTopUp: boolean =
            !!this.auction?.isProxyBiddingEnabled &&
            !!this.currentUserId &&
            storedLeaderIsCurrentUser;
        const optimisticBidCount: number = isProxyTopUp
            ? Number(this.lotItem.bidCount ?? 0)
            : Number(this.lotItem.bidCount ?? 0) + 1;
        if (lotState) {
            let highBidder: string | null | undefined = this.lotItem.highBidder;
            if (lotState.isHighBidder) {
                highBidder = this.currentUsername || highBidder;
            } else if (storedLeaderIsCurrentUser) {
                highBidder = null;
            } else if (this.currentUsername) {
                highBidder = reconcileHighBidder(
                    highBidder,
                    this.currentUsername,
                    false,
                );
            }
            this.lotItem = {
                ...this.lotItem,
                highBidderId: reconcileHighBidder(
                    this.lotItem.highBidderId,
                    this.currentUserId,
                    lotState.isHighBidder,
                ),
                highBidder,
                currentPrice: lotState.currentBid,
                minBidAmount: lotState.minimumNextBid,
                toDatetime: lotState.toDatetime,
                bidCount: optimisticBidCount,
                maximumProxyBid: Math.max(
                    this.lotItem.maximumProxyBid ?? 0,
                    amount,
                ),
            };
            this.rebuildView();
            return;
        }
        // When proxy bidding is on and another user already holds the high bid,
        // their bid may be backed by a hidden proxy max that instantly
        // out-counters ours. We can't know that max locally, so claiming the
        // high bid here flashes "You're winning" until the proxy's auto-counter
        // arrives (via PAGE_LOAD) and flips it to "You've been outbid" — the
        // bug. Leave the high bidder untouched and let the socket resolve it.
        // Every other case (fresh lot, raising our own max, beating a non-proxy
        // bid) is a guaranteed win, so claim it to avoid a false "outbid" flash.
        const outbidByProxy: boolean =
            !!this.auction?.isProxyBiddingEnabled &&
            !!this.lotItem.highBidderId &&
            this.lotItem.highBidderId !== this.currentUserId;
        this.lotItem = {
            ...this.lotItem,
            highBidderId: outbidByProxy
                ? this.lotItem.highBidderId
                : this.currentUserId,
            // Same unmasked-own-name rule as the lotState branch above.
            highBidder:
                !outbidByProxy && this.currentUsername
                    ? this.currentUsername
                    : this.lotItem.highBidder,
            bidCount: optimisticBidCount,
            maximumProxyBid: Math.max(
                this.lotItem.maximumProxyBid ?? 0,
                amount,
            ),
        };
        this.rebuildView();
    }

    // --- Watchlist ----------------------------------------------------------

    toggleWatch(): void {
        if (!this.isLoggedIn) {
            this.goSignIn();
            return;
        }
        const auctionLotsId: string | undefined = this.lotItem.auctionLotsId;
        if (!auctionLotsId) {
            return;
        }
        if (this.lotItem.watcherId) {
            const watcherId: string = this.lotItem.watcherId;
            this.watchlistService
                .removeFromWatchlist(watcherId, this.lotItem.title)
                .then(() => {
                    this.setWatcher('');
                    this.store.dispatch(
                        removeFromWatchlist({ watchlistId: auctionLotsId }),
                    );
                    this.utils.removeFromWatchlist(auctionLotsId);
                });
            return;
        }
        this.watchlistService
            .addToWatchlist(this.auctionId, auctionLotsId, this.lotItem.title)
            .subscribe((watcherId: string) => {
                this.setWatcher(watcherId);
                this.store.dispatch(
                    addToWatchlist({ watchlistId: auctionLotsId }),
                );
            });
    }

    private setWatcher(watcherId: string): void {
        this.lotItem = { ...this.lotItem, watcherId };
        this.rebuildView();
    }

    // --- Panel intents ------------------------------------------------------

    goSignIn(): void {
        // Carry the current page so login returns the user here, not /home.
        this.router.navigate(['/login'], {
            queryParams: { returnUrl: this.router.url },
        });
    }

    onRemind(): void {
        if (this.isLoggedIn) {
            this.open('alerts');
        } else {
            this.goSignIn();
        }
    }

    // --- Dialogs ------------------------------------------------------------

    open(dialog: ProductDialog): void {
        this.activeDialog = dialog;
    }

    closeDialog(): void {
        this.activeDialog = null;
    }

    sendMessageToSeller(): void {
        this.chatBoxService.open();
    }

    // --- Prev/next lot navigation --------------------------------------------

    /** Loads the neighbouring lots (catalog order) for the breadcrumb's
     *  prev/next buttons. Runs once per lot commit — socket-driven re-commits
     *  of the same lot are skipped, and a late response for a lot the user has
     *  already navigated away from is dropped. Non-critical enrichment: on
     *  error the page simply renders without the buttons. */
    private fetchAdjacentLots(): void {
        const auctionLotsId: string | undefined = this.lotItem.auctionLotsId;
        if (!auctionLotsId || this.adjacentFetchedForId === auctionLotsId) {
            return;
        }
        this.adjacentFetchedForId = auctionLotsId;
        this.auctionService.getAdjacentAuctionLots(auctionLotsId).subscribe({
            next: ({ data }: GetAdjacentAuctionLots200Response) => {
                if (this.lotItem?.auctionLotsId === auctionLotsId) {
                    this.adjacentLots = data;
                }
            },
            error: () => {
                this.adjacentLots = null;
            },
        });
    }

    goToAdjacentLot(lot: AdjacentAuctionLot | null): void {
        if (!lot) {
            return;
        }
        this.router.navigate(['/auctions', this.auctionId, 'lot', lot.lotId]);
    }

    /** Button label for a neighbouring lot — its catalog number when it has
     *  one (same "Lot N" form as the breadcrumb crumb), else a plain fallback. */
    adjacentLotLabel(lot: AdjacentAuctionLot | null, fallback: string): string {
        return lot && lot.number != null
            ? `Lot ${lot.number}${lot.suffix ?? ''}`
            : fallback;
    }

    // --- Breadcrumb ---------------------------------------------------------

    get crumbs(): string[] {
        const lotLabel: string =
            this.lotItem.number != null
                ? `Lot ${this.lotItem.number}${this.lotItem.suffix ?? ''}`
                : (this.lotItem.title ?? '');
        const trail: string[] = [this.translate.instant('LotDetails.auctions')];
        if (this.auction?.name) {
            trail.push(this.auction.name);
        }
        trail.push(lotLabel);
        return trail;
    }

    back(): void {
        // Go back to the previous page (the lot list, on whatever page/filter
        // the user left it) rather than resetting to the auction's page 1.
        // Fall back to the auction list when there's no in-app history to
        // return to (e.g. the lot was opened via a direct/deep link).
        if (window.history.length > 1) {
            this.location.back();
        } else {
            this.router.navigate(['/auctions', this.auctionId]);
        }
    }

    gotoCrumb(index: number): void {
        // 0 = all auctions, 1 = this auction's lot list (the final crumb is the
        // current lot and isn't clickable).
        if (index === 0) {
            this.router.navigate(['/auctions']);
        } else if (index === 1) {
            this.router.navigate(['/auctions', this.auctionId]);
        }
    }

    // --- Sub-feature data ---------------------------------------------------

    addNotes(note: LotItemUserNote): void {
        this.lotItemUserNotes.push(note);
        if (!this.watchlistId && !this.lotItem.watcherId) {
            this.addLotToWatchList();
        }
    }

    removeNote(noteId: string): void {
        this.lotItemUserNotes = this.lotItemUserNotes.filter(
            (item: LotItemUserNote) => item.id !== noteId,
        );
    }

    updateNote(editedNote: LotItemUserNote): void {
        this.lotItemUserNotes = this.lotItemUserNotes.map(
            (note: LotItemUserNote) =>
                note.id === editedNote.id ? editedNote : note,
        );
    }

    onRemindersChange(reminders: Reminder[]): void {
        this.savedReminders = reminders;
    }

    private getUserNotes(lotId: string): Observable<ListResponse> {
        return this.auctionService
            .getLotItemUserNotesByLotsId(1, -1, lotId)
            .pipe(
                tap((res: ListResponse) => {
                    this.lotItemUserNotes = res.data as LotItemUserNote[];
                }),
                catchError(() => {
                    this.snackbarService.error(
                        this.translate.instant('LotDetails.getUserNotesError'),
                    );
                    return EMPTY;
                }),
            );
    }

    private getReminders(auctionLotsId: string): Observable<ListResponse> {
        return this.notificationsService
            .getRemindersByAccountMappingsId(1, -1, auctionLotsId)
            .pipe(
                tap((res: ListResponse) => {
                    this.savedReminders = res.data ?? [];
                }),
                catchError(() => {
                    this.snackbarService.error(
                        this.translate.instant('LotDetails.getRemindersError'),
                    );
                    return EMPTY;
                }),
            );
    }

    private addLotToWatchList(): void {
        this.watchlistService
            .addToWatchlist(
                this.auctionId,
                this.lotItem.auctionLotsId ?? '',
                this.lotItem.title,
            )
            .subscribe({
                next: (watchlistId: string) => {
                    this.watchlistId = watchlistId;
                    this.setWatcher(watchlistId);
                },
                error: () => {
                    this.snackbarService.error(
                        this.translate.instant(
                            'LotDetails.addToWatchlistError',
                        ),
                    );
                },
            });
    }

    // --- Derived view-models ------------------------------------------------

    get secondsLeft(): number {
        const id: string = this.mappedLot.auctionLotsId ?? '';
        return this.countdownTracker.secondsLeft[id] ?? 0;
    }

    get watched(): boolean {
        return !!this.lotItem.watcherId;
    }

    get reserveAmount(): number {
        return Number(this.lotItem.reserveAmount ?? 0);
    }

    get reserveNotMet(): boolean {
        return (
            this.reserveAmount > 0 &&
            this.utils.checkReserveAmountMet(
                Number(this.lotItem.currentPrice ?? 0),
                this.reserveAmount,
            )
        );
    }

    /** Detail-page chips are deliberately ALL neutral: state colours here
     *  (green "Live", green "Reserve met", amber "Reserve not met") read as
     *  bidding-status signals and clash with the winning/outbid treatment —
     *  the label text alone carries the information. */
    get statusChip(): { tone: ChipTone; label: string } {
        if (this.isClosed) {
            return {
                tone: 'neutral',
                label: this.translate.instant('LotDetails.closed'),
            };
        }
        if (
            isTimeAhead(this.mappedAuction.fromDatetime, this.serverClock.now())
        ) {
            return {
                tone: 'neutral',
                label: this.translate.instant('LotDetails.upcoming'),
            };
        }
        return {
            tone: 'neutral',
            label: this.translate.instant('LotDetails.live'),
        };
    }

    /** Live lots chip met/not-met; closed lots only flag a failed reserve —
     *  "Reserve met" on a closed lot adds nothing over the closed/outcome
     *  signals, while "not met" explains why nobody won. */
    get showReserveChip(): boolean {
        return this.reserveAmount > 0 && (!this.isClosed || this.reserveNotMet);
    }

    get reserveChip(): { tone: ChipTone; label: string } {
        return this.reserveNotMet
            ? {
                  tone: 'neutral',
                  label: this.translate.instant('LotDetails.reserveNotMet'),
              }
            : {
                  tone: 'neutral',
                  label: this.translate.instant('LotDetails.reserveMet'),
              };
    }

    get attributes(): LotAttributes[] {
        // The canonical client types attributes loosely as object[]; the wire
        // rows are LotAttributes.
        return this.lotItem.category === 'Vehicles'
            ? ((this.lotItem.attributes as LotAttributes[] | undefined) ?? [])
            : [];
    }

    get highestBidder(): string {
        return this.lotItem.highBidder ?? '';
    }

    /** Mono eyebrow for dialogs — situates them on the current lot. */
    get lotCodeLabel(): string {
        return this.lotItem.number != null
            ? `LOT ${this.lotItem.number}${this.lotItem.suffix ?? ''}`
            : '';
    }

    get canViewBidHistory(): boolean {
        return (
            this.isLoggedIn &&
            !!this.highestBidder &&
            (!this.isClosed || !!this.auction?.isDisplayFinalPrice)
        );
    }

    get isProxyBid(): boolean {
        return !!this.lotItem.isHighestBidAProxy;
    }

    /** Offers are only valid while bidding is live — not upcoming, not closed
     *  or past the lot's end time. */
    private get isLotOpen(): boolean {
        const nowMs: number = this.serverClock.now();
        return (
            !this.isClosed &&
            !hasTimePassed(this.lotItem.toDatetime, nowMs) &&
            !isTimeAhead(this.mappedAuction.fromDatetime, nowMs)
        );
    }

    /** Same offer gate as vb-lot-card: the lot's own flag wins, falling back
     *  to the auction-wide setting — and only while the lot is open. */
    get showMakeAnOffer(): boolean {
        return (
            this.isLotOpen &&
            (this.lotItem.isAcceptOffers ??
                this.auction?.isMakeOfferEnabled ??
                false)
        );
    }

    get sanitizedDescription(): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(
            this.lotItem.description ?? '',
        );
    }

    // --- Sockets ------------------------------------------------------------

    private handleSocketEvent(data: string): void {
        let event: { action?: string; data?: unknown };
        try {
            event = JSON.parse(data);
        } catch {
            return;
        }
        const action: string | undefined = event.action;
        const payload: Record<string, unknown> | undefined =
            event.data && typeof event.data === 'object'
                ? (event.data as Record<string, unknown>)
                : undefined;

        if (
            action === 'AUCTION_UPDATE' &&
            payload &&
            payload['id'] &&
            payload['id'] === this.auction?.id
        ) {
            this.auction = {
                ...this.auction,
                ...(payload as unknown as AuctionDetail),
            };
            this.rebuildView();
            return;
        }

        if (
            action === 'BID_INCREMENT_UPDATE' &&
            (!event.data || event.data === this.auctionId)
        ) {
            this.utils.showMinimumBidChangedDialog(this.modalService);
            return;
        }

        if (action === 'PAGE_LOAD' && payload && payload['auctionLotsId']) {
            const patch: Partial<LotItemModel> = {
                toDatetime: payload['endDate'] as string,
                minBidAmount: payload['minimumBid'] as number,
                highBidderId: (payload['highBidder'] as string) ?? '',
                reserveAmount: payload['reserveAmount'] as number,
                currentPrice: payload['currentPrice'] as number,
            };
            // Keep the "(Proxy)" tag beside the high bidder live. Real-bid
            // broadcasts carry the new winning bid's flag; the refresh path
            // (rejected bid, deleted bid) resolves the standing high bid's
            // flag server-side.
            if (payload['isProxy'] != null) {
                patch.isHighestBidAProxy = Boolean(payload['isProxy']);
            }
            // The high-bidder display name gates the "view bid history" link —
            // without it the first live bid leaves the link hidden until a
            // refresh. Only override when present so we don't clear it.
            // Broadcasts can't personalize, so the name arrives MASKED for
            // everyone; the server's REST rule (resolveVisibleUsername) shows
            // the high bidder their real username, so apply the same rule
            // here when the winner is the viewer.
            if (payload['highBidderName'] != null) {
                const viewerIsHighBidder: boolean =
                    !!this.currentUserId &&
                    payload['highBidder'] === this.currentUserId;
                patch.highBidder =
                    viewerIsHighBidder && this.currentUsername
                        ? this.currentUsername
                        : (payload['highBidderName'] as string);
            }
            // Don't take numberOfWatchers from PAGE_LOAD: it's computed before a
            // bid's auto-watch is added, so it's stale and would clobber the
            // authoritative WATCHER_COUNT_UPDATE broadcast. Socket payloads
            // serialize bigint counts as strings — coerce. Bid counts only ever
            // grow, so accept a socket count only when it RAISES the one we
            // already show: a closed lot's PAGE_LOAD can report 0 (or a stale
            // lower value), which would otherwise blank a real count to
            // "0 bids" until a refresh — the reported bug.
            const incomingBidCount: number = Number(payload['bidCount']);
            if (
                payload['bidCount'] != null &&
                Number.isFinite(incomingBidCount) &&
                incomingBidCount >
                    this.knownBidCount(payload['auctionLotsId'] as string)
            ) {
                patch.bidCount = incomingBidCount;
            }
            if (
                this.applySocketPatch(payload['auctionLotsId'] as string, patch)
            ) {
                this.store.dispatch(
                    new LotFetchedSuccessfulAction({ result: this.lotItem }),
                );
            }
            return;
        }

        if (
            action === 'WATCHER_COUNT_UPDATE' &&
            payload &&
            payload['auctionLotsId']
        ) {
            if (
                this.applySocketPatch(payload['auctionLotsId'] as string, {
                    numberOfWatchers: payload['numberOfWatchers'] as number,
                })
            ) {
                this.rebuildView();
            }
            return;
        }

        if (
            action === 'USER_MAX_BID_UPDATE' &&
            payload &&
            payload['auctionLotId']
        ) {
            const userMaxBid: number = payload['userMaxBid'] as number;
            if (
                this.applySocketPatch(payload['auctionLotId'] as string, {
                    maximumProxyBid: userMaxBid,
                })
            ) {
                this.userHasBid = userMaxBid > 0;
                this.rebuildView();
            }
            return;
        }

        // Bid auto-watches the bidder.
        // Only set the viewer's own watch state; the count comes from the
        // separate WATCHER_COUNT_UPDATE broadcast.
        if (
            action === 'NEW_WATCHER_FROM_BID' &&
            payload &&
            payload['auctionLotId'] &&
            payload['accountMappingsId'] === this.currentUserId
        ) {
            const watcher: GetWatcherByIdResponse =
                payload as unknown as GetWatcherByIdResponse;
            const watcherId: string = watcher.id;
            const auctionLotId: string = watcher.auctionLotId;
            // A bid auto-creates a watcher the first time, so bump the header
            // watchlist count. Guard against re-counting if the socket replays
            // the event (e.g. on reconnect) — only count when not already watched.
            const alreadyWatching: boolean = this.utils.watchList.some(
                (w: GetWatcherByIdResponse) => w.auctionLotId === auctionLotId,
            );
            if (this.applySocketPatch(auctionLotId, { watcherId })) {
                this.utils.updateWatchlist(watcher, watcherId);
                if (!alreadyWatching) {
                    this.store.dispatch(
                        addToWatchlist({ watchlistId: auctionLotId }),
                    );
                }
                this.rebuildView();
            }
        }
    }

    /**
     * Applies a socket-pushed field patch to the current lot. While a lot fetch
     * is in flight, the patch is buffered (keyed by auctionLotsId) instead and
     * overlaid when the lot commits in onLotState — so an update that lands
     * mid-load can't be lost to the snapshot, and a stale lot can't show
     * "winning" after a socket already said "outbid". Returns true only when the
     * patch was applied to the live current lot (caller then dispatches/rebuilds).
     */
    private applySocketPatch(
        auctionLotsId: string | undefined,
        patch: Partial<LotItemModel>,
    ): boolean {
        if (!auctionLotsId) return false;
        if (this.lotLoadInFlight) {
            this.socketPatchesDuringLoad.set(auctionLotsId, {
                ...this.socketPatchesDuringLoad.get(auctionLotsId),
                ...patch,
            });
            return false;
        }
        if (auctionLotsId !== this.lotItem.auctionLotsId) return false;
        this.lotItem = { ...this.lotItem, ...patch };
        return true;
    }

    /**
     * The bid count we currently show, preferring a patch buffered during an
     * in-flight load over the snapshot. Lets the socket handler keep the count
     * monotonic — updates may only raise it, never lower it (so a closed lot's
     * 0-count snapshot can't blank a real count to "0 bids").
     */
    private knownBidCount(auctionLotsId: string | undefined): number {
        if (!auctionLotsId) return 0;
        const buffered: number | undefined =
            this.socketPatchesDuringLoad.get(auctionLotsId)?.bidCount;
        const current: unknown =
            buffered ??
            (auctionLotsId === this.lotItem.auctionLotsId
                ? this.lotItem.bidCount
                : undefined);
        const n: number = Number(current);
        return Number.isFinite(n) ? n : 0;
    }
}
